import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, User, Phone, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import axios from "axios";

import { formatPhoneInput } from "../../utils/phoneUtils";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const PortalRegister = () => {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", password: "", confirmPassword: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") {
      setFormData({ ...formData, phone: formatPhoneInput(value) });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) { toast.error("Passwords do not match"); return; }
    if (formData.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/portal/auth/register`, { name: formData.name, email: formData.email, phone: formData.phone || null, password: formData.password });
      localStorage.setItem("portal_token", res.data.token);
      toast.success("Registration successful!");
      navigate("/portal");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="portal-register-page">
      {/* Left Decorative Panel */}
      <div className="hidden lg:flex lg:w-[45%] relative overflow-hidden items-center justify-center" style={{background: 'linear-gradient(135deg, #0046BE 0%, #0033A0 100%)'}}>
        <div className="absolute inset-0 opacity-[0.05]" style={{backgroundImage: 'radial-gradient(circle at 30% 40%, white 1px, transparent 1px)', backgroundSize: '24px 24px'}} />
        <div className="relative z-10 px-12 max-w-md">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-8" style={{background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)'}}>
            <span className="text-xl font-black text-white">F</span>
          </div>
          <h2 className="text-3xl font-bold text-white font-['Outfit'] leading-tight">Get started<br />in seconds.</h2>
          <p className="text-blue-100/60 mt-4 text-sm leading-relaxed">Create your account to access your service portal and stay updated on every job.</p>
        </div>
      </div>

      {/* Right Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 bg-[#f8f9fb]">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-1.5 text-slate-300 hover:text-slate-500 text-sm mb-8 transition-colors" data-testid="portal-register-back-link">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to main site
          </Link>

          <div className="mb-6">
            <h1 className="text-2xl font-bold text-slate-800 font-['Outfit']">Create Account</h1>
            <p className="text-slate-400 mt-1.5 text-sm">Register to access your customer portal</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3.5">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400 font-medium">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                <Input id="name" name="name" type="text" placeholder="John Doe" value={formData.name} onChange={handleChange} className="h-11 pl-10 rounded-xl border-slate-200 focus:border-[#0046BE] focus:ring-[#0046BE]/10 text-slate-700 placeholder:text-slate-300" required data-testid="portal-name-input" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400 font-medium">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                <Input id="email" name="email" type="email" placeholder="you@example.com" value={formData.email} onChange={handleChange} className="h-11 pl-10 rounded-xl border-slate-200 focus:border-[#0046BE] focus:ring-[#0046BE]/10 text-slate-700 placeholder:text-slate-300" required data-testid="portal-email-input" />
              </div>
              <p className="text-[11px] text-slate-300">Use the email your service provider has on file</p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400 font-medium">Phone (Optional)</Label>
              <div className="relative">
                <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                <Input id="phone" name="phone" type="tel" placeholder="(555) 123-4567" value={formData.phone} onChange={handleChange} className="h-11 pl-10 rounded-xl border-slate-200 focus:border-[#0046BE] focus:ring-[#0046BE]/10 text-slate-700 placeholder:text-slate-300" data-testid="portal-phone-input" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400 font-medium">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                <Input id="password" name="password" type={showPassword ? "text" : "password"} placeholder="Min. 6 characters" value={formData.password} onChange={handleChange} className="h-11 pl-10 pr-10 rounded-xl border-slate-200 focus:border-[#0046BE] focus:ring-[#0046BE]/10 text-slate-700 placeholder:text-slate-300" required data-testid="portal-password-input" />
                <button type="button" className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-400 transition-colors" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400 font-medium">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                <Input id="confirmPassword" name="confirmPassword" type={showPassword ? "text" : "password"} placeholder="Confirm your password" value={formData.confirmPassword} onChange={handleChange} className="h-11 pl-10 rounded-xl border-slate-200 focus:border-[#0046BE] focus:ring-[#0046BE]/10 text-slate-700 placeholder:text-slate-300" required data-testid="portal-confirm-password-input" />
              </div>
            </div>

            <Button type="submit" className="w-full h-11 rounded-xl bg-[#0046BE] hover:bg-[#0033A0] text-white font-medium text-sm mt-1" disabled={loading} data-testid="portal-register-btn">
              {loading ? "Creating Account..." : "Create Account"}
            </Button>
          </form>

          <div className="mt-5 text-center">
            <p className="text-slate-400 text-sm">
              Already have an account?{" "}
              <Link to="/portal/login" className="text-[#0046BE] hover:text-[#0033A0] font-medium transition-colors">Sign In</Link>
            </p>
          </div>

          <p className="text-center text-slate-300 text-xs mt-6">
            Powered by <span className="font-medium text-slate-400">Fieldz AI</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PortalRegister;
